import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

interface CallOutcomeBreakdownProps {
  data: {
    completed: number
    missed: number
    voicemail: number
    failed: number
    cancelled: number
    bookings?: number
  }
  loading?: boolean
}

const outcomes = [
  { key: 'completed', label: 'Completed', color: 'bg-[#4d71fd]' },
  { key: 'bookings', label: 'Bookings Made', color: 'bg-emerald-500' },
  { key: 'missed', label: 'Missed', color: 'bg-rose-400' },
  { key: 'voicemail', label: 'Voicemail', color: 'bg-amber-400' },
  { key: 'failed', label: 'Failed', color: 'bg-rose-600' },
  { key: 'cancelled', label: 'Cancelled', color: 'bg-slate-400' },
] as const

export default function CallOutcomeBreakdown({ data, loading = false }: CallOutcomeBreakdownProps) {
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Call Outcomes</CardTitle>
          <CardDescription>Breakdown by call result</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <div className="animate-pulse text-muted-foreground">Loading...</div>
          </div>
        </CardContent>
      </Card>
    )
  }

  const total = Object.values(data).reduce((sum, v) => sum + v, 0)

  if (total === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Call Outcomes</CardTitle>
          <CardDescription>Breakdown by call result</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center text-center py-8">
            <p className="text-muted-foreground font-medium">No call data yet</p>
            <p className="text-xs text-muted-foreground mt-1 max-w-xs">
              Call outcomes (completed, missed, voicemail, etc.) will appear here once calls are made through your AI agents.
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Call Outcomes</CardTitle>
        <CardDescription>Breakdown by call result</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {outcomes.map(({ key, label, color }) => {
            const value = data[key] || 0
            const pct = total > 0 ? (value / total) * 100 : 0
            return (
              <div key={key}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="font-medium">{label}</span>
                  <span className="text-muted-foreground">{value} ({pct.toFixed(1)}%)</span>
                </div>
                <div className="h-2.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${color}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
